import { createFileRoute, useNavigate, useRouter, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { reportClientOpsEvent } from "@/lib/ops-log.functions";
import { getMyRoles } from "@/lib/roles.functions";

function reportAuthEvent(
  severity: "info" | "warn" | "error",
  code: string,
  message: string,
  context?: Record<string, unknown>,
) {
  // Fire-and-forget; never let logging failures affect the sign-in UX.
  reportClientOpsEvent({ data: { category: "auth", severity, code, message, context } }).catch(
    (err) => console.warn("[ops] report failed", err),
  );
  const line = `[auth:${severity}] ${code} — ${message}`;
  if (severity === "error") console.error(line, context ?? {});
  else if (severity === "warn") console.warn(line, context ?? {});
  else console.log(line, context ?? {});
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(Object.assign(new Error("timeout"), { code: "timeout" })), ms);
    p.then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
  });
}

function friendlyAuthError(e: any): string {
  const code = e?.code ?? "";
  const msg = String(e?.message ?? "");
  if (code === "timeout") return "El servidor tardó demasiado en responder. Revisa tu conexión e inténtalo de nuevo.";
  if (code === "invalid_credentials" || /invalid login credentials/i.test(msg)) return "Correo o contraseña incorrectos.";
  if (code === "email_not_confirmed" || /email not confirmed/i.test(msg)) return "Aún no confirmas tu correo. Revisa tu bandeja de entrada.";
  if (code === "user_already_exists" || /already registered/i.test(msg)) return "Ya existe una cuenta con ese correo. Inicia sesión.";
  if (code === "weak_password" || /password/i.test(msg) && /least|weak/i.test(msg)) return "La contraseña es demasiado débil. Usa al menos 6 caracteres.";
  if (e?.status === 429 || /rate limit/i.test(msg)) return "Demasiados intentos. Espera unos minutos e inténtalo de nuevo.";
  if (/fetch|network/i.test(msg)) return "Sin conexión con el servidor. Revisa tu internet e inténtalo de nuevo.";
  return msg || "No se pudo iniciar sesión. Inténtalo de nuevo.";
}

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Acceso — Mostrando México" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [canResend, setCanResend] = useState(false);
  const [resendWait, setResendWait] = useState(0);
  useEffect(() => {
    if (resendWait <= 0) return;
    const t = setTimeout(() => setResendWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [resendWait]);

  async function forgotPassword() {
    if (resendWait > 0) return;
    if (!email.includes("@")) {
      setError("Escribe tu correo arriba y vuelve a pulsar «¿Olvidaste tu contraseña?».");
      return;
    }
    setResendWait(60);
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (err) throw err;
      setError("Si existe una cuenta con ese correo, te enviamos un enlace para crear una nueva contraseña.");
    } catch (e: any) {
      setError(e?.status === 429 ? "Demasiados intentos. Espera unos minutos e inténtalo de nuevo." : "No se pudo enviar el correo. Inténtalo más tarde.");
    }
  }

  async function resendConfirmation() {
    if (resendWait > 0 || !email.includes("@")) return;
    setResendWait(60);
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { error: err } = await supabase.auth.resend({
        type: "signup",
        email,
        options: { emailRedirectTo: `${window.location.origin}${requestedNextPath() ?? "/mi-cuenta"}` },
      });
      if (err) throw err;
      setError("Te reenviamos el correo de confirmación. Revisa también la carpeta de spam.");
    } catch (e: any) {
      setError(e?.status === 429 ? "Demasiados intentos. Espera unos minutos e inténtalo de nuevo." : "No se pudo reenviar el correo. Verifica que la dirección esté bien escrita.");
    }
  }
  const navigate = useNavigate();
  const router = useRouter();

  // Post-auth destination: honor ?next=<same-origin-path> so tokenized invite
  // links (e.g. /maya/invite/{token}) resume automatically after sign-in.
  // Without ?next the landing page depends on the user's real role: staff go to
  // the admin panel, everyone else to their account.
  function requestedNextPath(): string | null {
    if (typeof window === "undefined") return null;
    let raw = new URLSearchParams(window.location.search).get("next");
    // Sin ?next: vuelve a la última página pública visitada (guardada en __root).
    if (!raw) { try { raw = sessionStorage.getItem("mm_auth_return"); } catch { raw = null; } }
    if (!raw) return null;
    // Only allow same-origin relative paths — never redirect to external URLs.
    if (!raw.startsWith("/") || raw.startsWith("//") || /[\\\s\x00-\x1f]/.test(raw)) return null;
    try {
      const u = new URL(raw, window.location.origin);
      if (u.origin !== window.location.origin) return null;
      return u.pathname + u.search + u.hash;
    } catch {
      return null;
    }
  }

  async function resolveNextPath(): Promise<string> {
    const requested = requestedNextPath();
    if (requested) return requested;
    try {
      const me = await getMyRoles();
      // AAL1 con rol administrativo: el rol aún está oculto por RLS, pero el
      // servidor indica mfaPending → pantalla de código, nunca "Mi cuenta".
      if (me.mfaPending) {
        return me.hasVerifiedFactor ? "/admin/security/verify-mfa" : "/admin/security/setup-mfa";
      }
      return me.isAdmin ? "/admin" : "/mi-cuenta";
    } catch {
      return "/mi-cuenta";
    }
  }

  // Regreso desde Google (redirección completa): si ya hay sesión al cargar
  // /auth, se decide el destino según roles/MFA verificados en servidor.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      // Sesión local (sin ida y vuelta); las rutas privadas vuelven a verificarla.
      const { data } = await supabase.auth.getSession();
      if (cancelled || !data.session) return;
      const to = await resolveNextPath();
      if (!cancelled) router.history.replace(to);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const emailDomain = email.includes("@") ? email.split("@").pop() : null;
    try {
      // Lazy: keeps the auth SDK out of the main bundle.
      const { supabase } = await import("@/integrations/supabase/client");
      if (mode === "signup") {
        const { error: err } = await withTimeout(supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}${requestedNextPath() ?? "/mi-cuenta"}`,
          },
        }), 20000);
        if (err) throw err;
        reportAuthEvent("info", "SIGNUP_INITIATED", "Signup enviado (confirmación por email)", {
          emailDomain,
        });
        setError("Revisa tu correo para confirmar la cuenta.");
        setCanResend(true);
      } else {
        const { error: err } = await withTimeout(
          supabase.auth.signInWithPassword({ email, password }),
          20000,
        );
        if (err) throw err;
        reportAuthEvent("info", "SIGNIN_OK", "Inicio de sesión con contraseña OK", { emailDomain });
        // La consulta de roles nunca debe dejar el botón en "...".
        const to = await withTimeout(resolveNextPath(), 8000).catch(() => "/mi-cuenta");
        router.history.replace(to);
        return;
      }
    } catch (e: any) {
      const message = friendlyAuthError(e);
      setError(message);
      if (e?.code === "email_not_confirmed") setCanResend(true);
      reportAuthEvent(
        "warn",
        mode === "signup" ? "SIGNUP_FAILED" : "SIGNIN_FAILED",
        message,
        {
          emailDomain,
          status: e?.status ?? null,
          code: e?.code ?? e?.name ?? null,
        },
      );
    } finally {
      setLoading(false);
    }
  }

  async function signInWithProvider(provider: "google") {
    setError(null);
    setLoading(true);
    try {
      // Preserva ?next (p. ej. la pantalla de consentimiento OAuth) tras el
      // viaje de ida y vuelta al proveedor; si no, el usuario cae en "/".
      // Lazy: mantiene el SDK de auth fuera del chunk de entrada.
      const { lovable } = await import("@/integrations/lovable");
      const result = await lovable.auth.signInWithOAuth(provider, {
        // Vuelve a /auth (pública) conservando ?next; allí se resuelve el destino.
        redirect_uri: `${window.location.origin}/auth${
          requestedNextPath() ? `?next=${encodeURIComponent(requestedNextPath()!)}` : ""
        }`,
      });
      if (result.error) throw new Error(result.error.message ?? `No se pudo iniciar sesión con ${provider}`);
      if (result.redirected) {
        reportAuthEvent("info", "OAUTH_REDIRECT", `Redirigiendo a ${provider} OAuth`, { provider });
        return;
      }
      reportAuthEvent("info", "OAUTH_OK", `Inicio de sesión con ${provider} OK`, { provider });
      router.history.replace(await resolveNextPath());
    } catch (e: any) {
      const message = e?.message ?? "Error de autenticación";
      setError(message);
      reportAuthEvent("error", "OAUTH_FAILED", message, {
        provider,
        code: e?.code ?? e?.name ?? null,
      });
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto w-full max-w-md px-4 py-10 pb-[calc(env(safe-area-inset-bottom,0px)+140px)] sm:py-16 lg:max-w-lg">
        <div className="rounded-3xl border border-border/70 bg-card/80 p-6 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.35)] backdrop-blur-md sm:p-8">
          {/* Tabs: Iniciar sesión / Registrarse */}
          <div className="grid grid-cols-2 gap-1 rounded-full border border-border/70 bg-background/60 p-1">
            {(["signin", "signup"] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  setMode(m);
                  setError(null);
                }}
                aria-pressed={mode === m}
                className={`h-9 rounded-full text-[13px] font-semibold transition-colors ${
                  mode === m
                    ? "bg-primary text-primary-foreground shadow-[0_8px_20px_-8px_oklch(0.72_0.18_45/0.6)]"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {m === "signin" ? "Iniciar sesión" : "Registrarse"}
              </button>
            ))}
          </div>

          <h1 className="mt-6 font-display text-2xl font-semibold sm:text-3xl">
            {mode === "signin" ? "Iniciar sesión" : "Crear cuenta"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {mode === "signin"
              ? "Accede con Google o tu correo."
              : "Regístrate con Google o tu correo."}
          </p>

          {/* Apple Sign In deshabilitado a propósito: el proveedor no tiene
              credenciales cargadas y devolvía 400 "missing OAuth secret". */}
          <div className="mt-6 grid gap-3">
            <button
              type="button"
              onClick={() => signInWithProvider("google")}
              disabled={loading}
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-border bg-background text-sm font-semibold hover:bg-accent disabled:opacity-60"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"/>
                <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"/>
                <path fill="#FBBC05" d="M3.97 10.71A5.41 5.41 0 0 1 3.68 9c0-.59.1-1.17.29-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.04l3.01-2.33z"/>
                <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l3.01 2.33C4.68 5.17 6.66 3.58 9 3.58z"/>
              </svg>
              {mode === "signin" ? "Google" : "Registrarse con Google"}
            </button>
          </div>


          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-wider text-muted-foreground">
            <span className="h-px flex-1 bg-border" /> o <span className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</span>
              <input
                required
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 h-11 w-full rounded-xl border border-border bg-background px-3 text-sm outline-none focus:border-primary"
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Contraseña</span>
              <input
                required
                type="password"
                minLength={6}
                autoComplete={mode === "signin" ? "current-password" : "new-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 h-11 w-full rounded-xl border border-border bg-background px-3 text-sm outline-none focus:border-primary"
              />
            </label>
            {error && <p className="text-sm text-destructive">{error}</p>}
            {mode === "signin" && (
              <button
                type="button"
                onClick={forgotPassword}
                disabled={resendWait > 0}
                className="block text-sm text-primary underline disabled:opacity-50 disabled:no-underline"
              >
                {resendWait > 0 ? `¿Olvidaste tu contraseña? (${resendWait}s)` : "¿Olvidaste tu contraseña?"}
              </button>
            )}
            {canResend && (
              <button
                type="button"
                onClick={resendConfirmation}
                disabled={resendWait > 0}
                className="text-sm text-primary underline disabled:opacity-50 disabled:no-underline"
              >
                {resendWait > 0 ? `Reenviar correo de confirmación (${resendWait}s)` : "Reenviar correo de confirmación"}
              </button>
            )}
            <button
              type="submit"
              disabled={loading}
              className="inline-flex h-11 w-full items-center justify-center rounded-xl bg-primary text-sm font-semibold text-primary-foreground disabled:opacity-60"
            >
              {loading ? (mode === "signin" ? "Entrando…" : "Creando cuenta…") : mode === "signin" ? "Entrar" : "Crear cuenta"}
            </button>
          </form>

          <button
            type="button"
            onClick={() => {
              setMode(mode === "signin" ? "signup" : "signin");
              setError(null);
            }}
            className="mt-4 text-sm text-muted-foreground hover:text-foreground"
          >
            {mode === "signin" ? "¿No tienes cuenta? Regístrate" : "¿Ya tienes cuenta? Iniciar sesión"}
          </button>
        </div>

        <div className="mt-8 text-center">
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">← Volver al inicio</Link>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

